import React, { useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkle, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Insights() {
  const [report, setReport] = useState("");
  const [focus, setFocus] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setReport("");
    try {
      const res = await api.post("/insights/hiring", { focus: focus || null });
      setReport(res.data.report);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI failed to respond");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader
        overline="AI Layer · Claude Sonnet 4.6"
        title="Hiring & Staffing Insights"
        subtitle="Feed the pipeline + roster into Claude. Get concrete hiring recommendations, skill gaps and reallocation moves."
      />

      <div className="border border-blue-500/20 bg-gradient-to-br from-blue-500/[0.05] to-transparent rounded-lg p-6 mb-6">
        <div className="flex items-center gap-2 mb-3"><Sparkle size={16} weight="fill" className="text-[#0F1E38]" /><div className="overline text-[#0F1E38]">Ask the AI</div></div>
        <div className="flex flex-col md:flex-row gap-3">
          <Input data-testid="insights-focus" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Optional focus, e.g. 'we're chasing a big Palo Alto SASE deal in EU'" className="bg-slate-50 border-slate-300 flex-1" />
          <Button data-testid="generate-insights-btn" onClick={generate} disabled={loading} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-mono uppercase tracking-wider text-xs whitespace-nowrap">
            <Lightning size={14} className="mr-2" /> {loading ? "Analyzing..." : "Generate Report"}
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-lg p-8 min-h-[400px]">
        {loading && (
          <div className="grid-bg h-64 rounded flex items-center justify-center">
            <div className="text-center">
              <Sparkle size={32} weight="duotone" className="text-[#0F1E38] animate-pulse mx-auto" />
              <div className="mt-3 text-sm text-slate-500 font-mono uppercase tracking-wider">Reasoning across pipeline and bench...</div>
            </div>
          </div>
        )}
        {!loading && !report && (
          <div className="grid-bg h-64 rounded flex items-center justify-center text-center">
            <div>
              <div className="overline mb-3">No report yet</div>
              <div className="text-sm text-slate-500 max-w-md">Click <span className="text-[#0F1E38] font-mono">Generate Report</span> to get a data-driven staffing analysis based on your current projects, engineers, and field techs.</div>
            </div>
          </div>
        )}
        {!loading && report && (
          <article data-testid="insights-report" className="prose prose-invert prose-sm max-w-none">
            <MarkdownLite text={report} />
          </article>
        )}
      </div>
    </div>
  );
}

// Minimal markdown renderer — headings + bullets + bold, no external dep.
function MarkdownLite({ text }) {
  const lines = text.split("\n");
  const out = [];
  let list = [];
  const flush = () => { if (list.length) { out.push(<ul key={out.length} className="list-disc pl-6 space-y-1 my-3 text-slate-700">{list}</ul>); list = []; } };
  lines.forEach((l, i) => {
    if (/^#{3}\s+/.test(l)) { flush(); out.push(<h3 key={i} className="font-display text-lg font-bold text-[#0F1E38] mt-6 mb-2">{l.replace(/^###\s+/, "")}</h3>); }
    else if (/^##\s+/.test(l)) { flush(); out.push(<h2 key={i} className="font-display text-xl font-bold text-[#0F1E38] mt-6 mb-3">{l.replace(/^##\s+/, "")}</h2>); }
    else if (/^#\s+/.test(l)) { flush(); out.push(<h1 key={i} className="font-display text-2xl font-bold text-[#0F1E38] mt-6 mb-3">{l.replace(/^#\s+/, "")}</h1>); }
    else if (/^[-*]\s+/.test(l)) { list.push(<li key={i} dangerouslySetInnerHTML={{ __html: inlineFmt(l.replace(/^[-*]\s+/, "")) }} />); }
    else if (l.trim() === "") { flush(); }
    else { flush(); out.push(<p key={i} className="text-slate-700 my-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineFmt(l) }} />); }
  });
  flush();
  return <>{out}</>;
}
function inlineFmt(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#0F1E38]">$1</strong>')
    .replace(/`(.+?)`/g, '<code class="font-mono text-[#0F1E38] bg-blue-500/10 px-1 rounded">$1</code>');
}
